import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Copy, Check, Gift, Smartphone } from "lucide-react";
import { GeneralSetting } from "@apis/index";
import {
  PageContainer,
  Blob1,
  Blob2,
  Header,
  Logo,
  HeroContainer,
  HeroLeft,
  HeroRight,
  TagBadge,
  MainHeading,
  SubHeading,
  ReferralBox,
  ReferralLabel,
  CodeContainer,
  ReferralCode,
  CopyButton,
  CopiedText,
  ActionSection,
  PrimaryButton,
  VoucherCard,
  VoucherGlow,
  VoucherHeader,
  VoucherBadge,
  VoucherValue,
  VoucherFooter,
  SectionContainer,
  SectionTitle,
  StepsGrid,
  StepCard,
  StepIconWrapper,
  StepTitle,
  StepDescription,
  FooterContainer,
  FooterContent,
  FooterLinks,
  FooterLink,
  Copyright,
} from "./style";

export default function ReferralPage() {
  const [searchParams] = useSearchParams();
  const [copied, setCopied] = useState(false);
  const [deviceOS, setDeviceOS] = useState<"ios" | "android" | "desktop">(
    "desktop",
  );
  const referralCode = searchParams.get("code") || "ZEDFO2026";
  const [referralSettings, setReferralSettings] = useState({
    sharedCodeAmount: 10,
    useCodeAmount: 10,
  });
  const [appUrls, setAppUrls] = useState({
    playStoreUrl: `https://play.google.com/store/apps/details?id=app.inflancer.zedfo&referrer=${referralCode}`,
    appStoreUrl: "https://apps.apple.com/us/app/zedfo/id6764337145",
  });
  // https://play.google.com/store/apps/details?id=com.yourapp&referrer=promo%3DSAVE20
  useEffect(() => {
    const ua = navigator.userAgent;
    if (/android/i.test(ua)) {
      setDeviceOS("android");
    } else if (/iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream) {
      setDeviceOS("ios");
    } else {
      setDeviceOS("desktop");
    }

    const fetchSettings = async () => {
      try {
        const res = await GeneralSetting.getByKey({
          id: "customer-refer-setting",
          showErr: false,
        });
        if (res && res.infos) {
          setReferralSettings({
            sharedCodeAmount: Number(res.infos.sharedCodeAmount) || 10,
            useCodeAmount: Number(res.infos.useCodeAmount) || 10,
          });
        }
      } catch (err) {
        console.error("Failed to fetch referral settings:", err);
      }
    };

    const fetchAppUrls = async () => {
      try {
        const res = await GeneralSetting.getByKey({
          id: "app-version-user",
          showErr: false,
        });
        if (res && res.infos) {
          setAppUrls((prev) => ({
            playStoreUrl: res.infos.playStoreLink || prev.playStoreUrl,
            appStoreUrl: res.infos.appStoreLink || prev.appStoreUrl,
          }));
        }
      } catch (err) {
        console.error("Failed to fetch app download URLs:", err);
      }
    };

    fetchSettings();
    fetchAppUrls();
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralCode);
      setCopied(true);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const { playStoreUrl, appStoreUrl } = appUrls;

  const handleOpenOrInstall =
    (os: "ios" | "android") => (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (deviceOS === "desktop") {
        return; // Desktop users will just use standard href redirect link
      }

      e.preventDefault();
      const customScheme = `zedfo://referral?code=${referralCode}`;
      const storeUrl = os === "ios" ? appStoreUrl : playStoreUrl;

      // Attempt to open the app via custom scheme
      window.location.href = customScheme;

      // Fallback if app is not installed
      const fallbackTimer = setTimeout(() => {
        window.location.href = storeUrl;
      }, 1500);

      const handleVisibilityChange = () => {
        if (document.hidden) {
          clearTimeout(fallbackTimer);
          document.removeEventListener(
            "visibilitychange",
            handleVisibilityChange,
          );
        }
      };
      document.addEventListener("visibilitychange", handleVisibilityChange);
    };

  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copied]);

  // SVG Icons for Buttons
  const renderAppStoreIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 4.17c.66-.81 1.11-1.93.99-3.06-1 .04-2.22.67-2.94 1.51-.62.71-1.16 1.85-1.01 2.96 1.11.09 2.26-.57 2.96-1.41z" />
    </svg>
  );

  const renderPlayStoreIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3 20.5V3.5C3 2.3 3.9 1.5 5 1.5c.4 0 .7.1 1 .3L20.3 10c.8.5 1.2 1.4 1.2 2s-.4 1.5-1.2 2L6 22.2c-.3.2-.6.3-1 .3-1.1 0-2-.8-2-2zM5.3 4.2v15.6L17.7 12 5.3 4.2z" />
    </svg>
  );

  return (
    <PageContainer>
      <Blob1 />
      <Blob2 />

      {/* 🔹 Navigation Header */}
      <Header>
        <Logo src="/emailfooterlogo.svg" alt="Zedfo" />
      </Header>

      {/* 🔹 Hero Section */}
      <HeroContainer>
        <HeroLeft>
          <TagBadge>Invite Program</TagBadge>
          <MainHeading>You've Been Invited!</MainHeading>
          <SubHeading>
            Join Zedfo, Nepal's favorite e-commerce marketplace. Use the code
            below on sign up to unlock instant rewards of Rs.{" "}
            {referralSettings.useCodeAmount}!
          </SubHeading>

          {/* Referral Code Box */}
          <ReferralBox>
            <ReferralLabel>Your Referral Code</ReferralLabel>
            <CodeContainer>
              <ReferralCode>{referralCode}</ReferralCode>
              <CopyButton onClick={handleCopy} aria-label="Copy Referral Code">
                {copied ? (
                  <Check size={20} color="#10b981" />
                ) : (
                  <Copy size={20} />
                )}
              </CopyButton>
            </CodeContainer>
            <CopiedText visible={copied}>(Copied to clipboard)</CopiedText>
          </ReferralBox>

          {/* Primary Action Button */}
          <ActionSection>
            {(deviceOS === "ios" || deviceOS === "desktop") && (
              <PrimaryButton
                href={deviceOS === "desktop" ? appStoreUrl : "#"}
                target={deviceOS === "desktop" ? "_blank" : undefined}
                onClick={handleOpenOrInstall("ios")}
              >
                {renderAppStoreIcon()}
                {deviceOS === "desktop"
                  ? "App Store"
                  : "Open or Install from App Store"}
              </PrimaryButton>
            )}
            {(deviceOS === "android" || deviceOS === "desktop") && (
              <PrimaryButton
                href={deviceOS === "desktop" ? playStoreUrl : "#"}
                target={deviceOS === "desktop" ? "_blank" : undefined}
                onClick={handleOpenOrInstall("android")}
              >
                {renderPlayStoreIcon()}
                {deviceOS === "desktop"
                  ? "Google Play"
                  : "Open or Install from Google Play"}
              </PrimaryButton>
            )}
          </ActionSection>
        </HeroLeft>

        <HeroRight>
          {/* Glassmorphism Voucher Mockup */}
          <VoucherCard>
            <VoucherGlow />
            <VoucherHeader>
              <VoucherBadge>Welcome Bonus</VoucherBadge>
              <Gift size={24} color="#ff3135" />
            </VoucherHeader>
            <VoucherValue>Rs. {referralSettings.useCodeAmount}</VoucherValue>
            <VoucherFooter>
              Credited automatically on your first order. Terms and conditions
              apply.
            </VoucherFooter>
          </VoucherCard>
        </HeroRight>
      </HeroContainer>

      {/* 🔹 How It Works Section */}
      <SectionContainer>
        <SectionTitle>Get Started in 3 Simple Steps</SectionTitle>
        <StepsGrid>
          {/* Step 1 */}
          <StepCard>
            <StepIconWrapper>
              <Copy size={24} />
            </StepIconWrapper>
            <StepTitle>1. Copy Code</StepTitle>
            <StepDescription>
              Copy the invite code above to your clipboard so it's ready.
            </StepDescription>
          </StepCard>

          {/* Step 2 */}
          <StepCard>
            <StepIconWrapper>
              <Smartphone size={24} />
            </StepIconWrapper>
            <StepTitle>2. Open or Get App</StepTitle>
            <StepDescription>
              Open App Store/Play Store to download the native Zedfo App.
            </StepDescription>
          </StepCard>

          {/* Step 3 */}
          <StepCard>
            <StepIconWrapper>
              <Gift size={24} />
            </StepIconWrapper>
            <StepTitle>3. Claim Rewards</StepTitle>
            <StepDescription>
              Paste the invite code during sign up to unlock your Rs.{" "}
              {referralSettings.useCodeAmount} welcome rewards.
            </StepDescription>
          </StepCard>
        </StepsGrid>
      </SectionContainer>

      {/* 🔹 Footer Section */}
      <FooterContainer>
        <FooterContent>
          <Logo src="/emailfooterlogo.svg" alt="Zedfo" />
          <FooterLinks>
            <FooterLink to="/privacy-policy">Privacy Policy</FooterLink>
            <FooterLink to="/terms-of-service">Terms of Service</FooterLink>
            <FooterLink to="/refund-policy">Refund Policy</FooterLink>
            <FooterLink to="/contact-us">Contact Us</FooterLink>
          </FooterLinks>
        </FooterContent>
        <Copyright>
          © 2026 Zedfo. All rights reserved. Registered trademark of Zedfo
          Nepal.
        </Copyright>
      </FooterContainer>
    </PageContainer>
  );
}
